import { Button, Icon } from "materia"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import type { PeerProfile } from "../../types"
import { PeerAvatar } from "../PeerAvatar"
import s from "./PeoplePicker.module.scss"

/** A colleague who can be messaged, as the directory reports them. */
export type Colleague = PeerProfile & {
  /** Platform user id — what starting a conversation is addressed by. */
  id: string
}

type PeoplePickerProps = {
  colleagues: Colleague[]
  /** One person means a direct message; more means a group. */
  onConfirm: (chosen: Colleague[]) => void
  /**
   * What the action says. Starting a chat states what the choice means, so it
   * defaults to that; adding people to a group says the same thing whatever
   * the count.
   */
  confirmLabel?: string
  title?: string
  /** Who is already chosen, when coming back to the picker. */
  initialChosen?: Colleague[]
}

export function PeoplePicker({
  colleagues,
  onConfirm,
  confirmLabel,
  title,
  initialChosen = [],
}: PeoplePickerProps) {
  const { t } = useTranslation()
  const [chosen, setChosen] = useState<Colleague[]>(initialChosen)
  const [search, setSearch] = useState("")

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return colleagues

    return colleagues.filter((colleague) =>
      colleague.displayName.toLowerCase().includes(needle)
    )
  }, [colleagues, search])

  const isChosen = (colleague: Colleague) =>
    chosen.some((one) => one.id === colleague.id)

  const toggle = (colleague: Colleague) => {
    setChosen((current) =>
      current.some((one) => one.id === colleague.id)
        ? current.filter((one) => one.id !== colleague.id)
        : [...current, colleague]
    )
    // Clear the filter after a pick, so the next name starts from the top.
    setSearch("")
  }

  return (
    <div className={s.picker}>
      <h2 className={s.picker__title}>{title ?? t("chat.NEW_CHAT")}</h2>

      <div className={s.picker__field}>
        {chosen.map((one) => (
          <button
            key={one.id}
            className={s.picker__chip}
            type="button"
            onClick={() => toggle(one)}
            aria-label={String(
              t("chat.REMOVE_PERSON_ARIA", { name: one.displayName })
            )}
          >
            {one.displayName}
            <span className={s.picker__chipRemove}>
              <Icon icon="Close" size="small" />
            </span>
          </button>
        ))}

        <input
          className={s.picker__search}
          value={search}
          placeholder={
            chosen.length > 0 ? "" : String(t("chat.PICK_PEOPLE_PLACEHOLDER"))
          }
          aria-label={String(t("chat.PICK_PEOPLE_PLACEHOLDER"))}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className={s.picker__people}>
        {matches.length === 0 ? (
          <p className={s.picker__empty}>{t("chat.NO_COLLEAGUES")}</p>
        ) : (
          matches.map((colleague) => (
            <button
              key={colleague.id}
              className={modifiers(s, "picker__person", {
                isSelected: isChosen(colleague),
              })}
              type="button"
              onClick={() => toggle(colleague)}
              aria-pressed={isChosen(colleague)}
            >
              <span className={s.picker__avatar}>
                <PeerAvatar {...colleague} size="medium" />
              </span>
              <span className={s.picker__name}>{colleague.displayName}</span>
              {isChosen(colleague) && (
                <span className={s.picker__check}>
                  <Icon icon="Checkmark" size="small" />
                </span>
              )}
            </button>
          ))
        )}
      </div>

      <Button
        theme="blue"
        size="big"
        borderRadius="24"
        disabled={chosen.length === 0}
        onClick={() => onConfirm(chosen)}
      >
        {/* One person is a conversation; more needs a name first. */}
        {confirmLabel ??
          (chosen.length > 1
            ? t("chat.CONTINUE_TO_GROUP")
            : t("chat.START_DIRECT"))}
      </Button>
    </div>
  )
}
